import { useEffect } from "react";
import type { Channel, Program } from "@hybrid-iptv/types";
import { dbAll } from "../storage/db.js";
import type { CatalogCacheEntry } from "../storage/catalogCache.js";
import type { EpgCacheEntry } from "../epg/epgCache.js";
import { useNotifications } from "../library/notificationsStore.js";
import { useFavoritesStore } from "../library/favoritesStore.js";
import { scanForNotifications } from "../library/notificationScan.js";
import { nativeAlertsAvailable, showNativeAlert } from "../library/nativeAlerts.js";
import { nativeGuideAvailable } from "../api/nativeGuide.js";
import { activeProviderSourceId, providerBody } from "../epg/api.js";
import { extraGuideUrls, guideTuning } from "../epg/guideFeeds.js";

/**
 * Section 12 notifications (steps 6 + 8) — renders nothing; lives in the
 * shell next to SyncScheduler.
 *
 * On an interval (while opted in) it reads the device caches — every
 * provider's channels + EPG, per spec step 8 "scans EPG across all sources"
 * — maps XMLTV programs back to our channel ids (same nameMapping path as
 * useProgramIndex) and, for programs starting within the window:
 *   - records every alert in the in-app inbox (topbar bell) — this works
 *     regardless of browser Notification permission, so a TV that can't (or
 *     won't) show OS notifications still surfaces alerts
 *   - additionally fires a real browser notification when permission is
 *     granted (the roadmap's "done when" check)
 * Alerts are deduped and persisted in notificationsStore, so a reload never
 * re-fires the same minute of alerts.
 *
 * Browser notifications fire from background tabs too — that's the point of
 * "starting soon". Best-effort: if the tab is throttled or the device cache
 * is empty, the next tick simply finds nothing.
 */
const SCAN_INTERVAL_MS = 60_000;
/** How far ahead to look for "starting soon". */
const WINDOW_MS = 15 * 60_000;

export function NotificationEngine() {
  const enabled = useNotifications((s) => s.enabled);

  useEffect(() => {
    if (!enabled) return;
    // OS notifications are best-effort: the inbox works without permission. In the
    // app they are Android's (AlertsPlugin, a no-op when the viewer turned them off).
    const native = nativeAlertsAvailable();
    const canNotify = !native && typeof Notification !== "undefined" && Notification.permission === "granted";

    let cancelled = false;
    let inFlight = false;

    async function run(): Promise<void> {
      if (inFlight || cancelled) return;
      inFlight = true;
      try {
        const store = useNotifications.getState();
        store.sweepExpired(Date.now());
        const favoriteChannelIds = new Set(useFavoritesStore.getState().favoritesOf("channel"));
        const hiddenChannels = new Set(useFavoritesStore.getState().hiddenOf("channel"));
        const hiddenCategories = new Set(useFavoritesStore.getState().hiddenOf("category"));
        const trackedTitles = store.trackedList().map((t) => t.title);
        if (favoriteChannelIds.size === 0 && trackedTitles.length === 0) return;

        const { channels, channelNameById, hiddenSet } = await loadChannels(hiddenChannels, hiddenCategories);
        if (cancelled) return;
        const programs = await loadMappedPrograms(channels);
        if (cancelled) return;

        const alerts = scanForNotifications({
          programs,
          channelNameById,
          favoriteChannelIds,
          trackedTitles,
          now: new Date(),
          windowMs: WINDOW_MS,
        });

        // Exclude channels the user hid (system-hidden are already absent
        // from favoritesOf-driven UI, but a user-hide must never alert).
        for (const alert of alerts) {
          if (cancelled) return;
          const programHidden = alert.kind === "starting-soon" && hiddenSet.has(alert.channelName);
          if (programHidden) continue;
          if (store.isFired(alert.dedupeKey)) continue;

          // In-app inbox first — visible on TV with or without permission.
          store.pushAlert({
            id: alert.dedupeKey,
            kind: alert.kind,
            title: alert.title,
            channelName: alert.channelName,
            startsAt: alert.startsAt,
            message: alert.message,
          });

          // Then the OS notification, only when the browser allows it.
          if (native) {
            showNativeAlert({ title: alert.kind === "starting-soon" ? "Starting soon" : "Tracked title airing", body: alert.message, tag: alert.dedupeKey });
          } else if (canNotify) {
            try {
              new Notification(alert.kind === "starting-soon" ? "Starting soon" : "Tracked title airing", {
                body: alert.message,
                tag: alert.dedupeKey,
                icon: "/icon-192.png",
              });
            } catch {
              // Permission revoked mid-session or constructor rejected — the
              // inbox already has the alert; nothing else to do.
            }
          }
          store.markFired(alert.dedupeKey);
        }
      } finally {
        inFlight = false;
      }
    }

    // First scan shortly after mount, then on the interval.
    const first = window.setTimeout(() => void run(), 10_000);
    const interval = window.setInterval(() => void run(), SCAN_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(first);
      window.clearInterval(interval);
    };
  }, [enabled]);

  return null;
}

/** Load every provider's channels from the device cache into maps. */
async function loadChannels(
  hiddenChannels: Set<string>,
  hiddenCategories: Set<string>,
): Promise<{
  channels: Channel[];
  channelNameById: Map<string, string>;
  hiddenSet: Set<string>;
}> {
  const channels: Channel[] = [];
  const channelNameById = new Map<string, string>();
  const hiddenSet = new Set<string>();

  const rows = await dbAll<CatalogCacheEntry<Channel[]>>("channels").catch(() => []);
  for (const row of rows) {
    if (!row?.data || !Array.isArray(row.data)) continue;
    for (const ch of row.data) {
      if (!ch?.id || !ch?.name) continue;
      channelNameById.set(ch.id, ch.name);
      if (hiddenChannels.has(ch.id)) hiddenSet.add(ch.name);
      if (ch.categoryId && hiddenCategories.has(ch.categoryId)) hiddenSet.add(ch.name);
      channels.push(ch);
    }
  }
  return { channels, channelNameById, hiddenSet };
}

/**
 * In the APK the screens no longer keep the guide in the device cache (Phase 3
 * step 2a: each asks the native guide for its own channels), so the scan asks
 * it for what every channel has on from now to the end of WINDOW_MS, titles only.
 */
async function nativeEntries(names: string[]): Promise<Array<Pick<EpgCacheEntry, "data">>> {
  if (names.length === 0) return [];
  const { deviceGuideStarting } = await import("../api/deviceApi.js");
  const sourceId = activeProviderSourceId();
  const data = await deviceGuideStarting(sourceId, providerBody(), names, Date.now(), WINDOW_MS + 60_000, extraGuideUrls(sourceId), guideTuning(sourceId) ?? undefined).catch(
    () => null,
  );
  return data ? [{ data: data as unknown as EpgCacheEntry["data"] }] : [];
}

/**
 * Load every cached EPG window across providers and map XMLTV channel ids →
 * our catalog ids (the useProgramIndex nameMapping path). Returns the flat
 * program list with our channel ids; the scan uses channelNameById for names.
 */
async function loadMappedPrograms(channels: Channel[]): Promise<Program[]> {
  const nameToOurId = new Map<string, string>();
  for (const ch of channels) {
    const key = ch.name.toLowerCase().trim();
    if (key) nameToOurId.set(key, ch.id);
  }

  const entries = nativeGuideAvailable() ? await nativeEntries([...nameToOurId.keys()]) : await dbAll<EpgCacheEntry>("epgCache").catch(() => []);
  const mapped: Program[] = [];
  for (const entry of entries) {
    // Entries are EpgCacheEntry directly ({ fetchedAt, savedAt, data:
    // EpgResponse }) — NOT wrapped in CatalogCacheEntry. Reading data.data
    // here silently skipped every entry and the engine never fired a single
    // alert (real bug caught by driving the preview end to end).
    const data = entry?.data;
    if (!data || !Array.isArray(data.programs)) continue;
    const xmltvIdToOurIds = new Map<string, string[]>();
    if (data.nameMapping) {
      for (const [xmltvId, reqNames] of Object.entries(data.nameMapping)) {
        const ourIds: string[] = [];
        for (const reqName of reqNames) {
          const ourId = nameToOurId.get(reqName.toLowerCase().trim());
          if (ourId && !ourIds.includes(ourId)) ourIds.push(ourId);
        }
        if (ourIds.length > 0) xmltvIdToOurIds.set(xmltvId, ourIds);
      }
    }
    for (const p of data.programs) {
      const ourIds = xmltvIdToOurIds.get(p.channelId);
      if (!ourIds) continue;
      for (const ourId of ourIds) mapped.push({ ...p, channelId: ourId });
    }
  }
  return mapped;
}
